import { useEffect, useState } from 'react';
import { FC } from 'react';
import { notifyMsg } from '../../configs';
import {
  Checkbox,
  IconButton,
  LinearProgress,
  Stack,
  TablePagination,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import { Edit3, Trash2 } from 'react-feather';
import TableRow from '@mui/material/TableRow';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../components/TableTheme';
import { AlertBar } from '../../components/AlertBar';
import {
  BulkSelectionBar,
  managementTableSx,
} from '../../components/ManagementPageLayout';
import {
  useGetPaginateSchedulers,
  useDeleteSchedulers,
} from '../../api-http/scheduler';

import { useTable } from '../../util/useTable';
import { OrderEnum, SortEnum } from '../../types/pagination.types';
import { useSnack } from '../../util/useSnack';
import { useIsUserRole } from '../../util/useIsUserRole';
import { useConfirm } from 'material-ui-confirm';
import { UserRole } from '../../types/user.types';
import { SchedulerGetResponse } from '../../types/scheduler.types';
import { displayDate } from '../../util/dateTime';

import { NavLink } from 'react-router-dom';
import { SchedulerEditModal } from './components/SchedulerEditModal';
import { DialogModal } from '../../components';

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

interface SchedulerTableProp {
  refresh: boolean;
  schedulerSearchTxt: string;
}

export const SchedulerTable: FC<SchedulerTableProp> = ({
  refresh,
  schedulerSearchTxt,
}) => {
  const confirm = useConfirm();

  const disableDelete = useIsUserRole([UserRole.user, UserRole.dataEditor]);
  const { showErrSnack, showSuccessSnack } = useSnack();
  const { sortOnClick, pagingParams, tableProps } = useTable(true);
  const { sortby, order } = pagingParams;
  const [schedulerEditModal, setSchedulerEditModal] = useState(false);
  const {
    data: list,
    isLoading: listIsLoading,
    refetch,
    error,
  } = useGetPaginateSchedulers({ ...pagingParams, search: schedulerSearchTxt });

  const {
    error: deleteError,
    isLoading: isDeleteLoading,
    mutateAsync: deleteScheduler,
    data: deletedScheduler,
  } = useDeleteSchedulers({
    onError: () => showErrSnack(notifyMsg.DELETE_ATTEMPT_FAILED),
  });

  const [activeScheduler, setActiveScheduler] = useState<
    SchedulerGetResponse | undefined
  >();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    refetch();
    setSelectedIds(new Set());
  }, [deletedScheduler, refresh]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [schedulerSearchTxt, pagingParams.page]);

  const handleDelete = async (id: string) => {
    try {
      await confirm({
        description: notifyMsg.DELETE_CONFIRMATION,
        confirmationText: 'Yes',
      });
      await deleteScheduler({ id });
      setSelectedIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
      await refetch();
      showSuccessSnack(notifyMsg.SCHEDULER_DELETE_SUCCESS);
    } catch (e) {}
  };

  const schedulers = list?.data || [];
  const pageIds = schedulers.map((scheduler) => scheduler._id);
  const allSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someSelected = pageIds.some((id) => selectedIds.has(id));

  const setSelected = (id: string, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(pageIds) : new Set());
  };

  const removeSelected = async () => {
    if (!selectedIds.size) return;
    try {
      await confirm({
        title: `Delete ${selectedIds.size} schedules?`,
        description:
          'The selected schedules will be permanently deleted. This action cannot be undone.',
        confirmationText: 'Delete selected',
      });
      for (const id of Array.from(selectedIds)) {
        await deleteScheduler({ id });
      }
      setSelectedIds(new Set());
      await refetch();
      showSuccessSnack('Selected schedules deleted');
    } catch (e) {}
  };

  return (
    <>
      {(listIsLoading || isDeleteLoading) && <LinearProgress />}
      <AlertBar severity="error" msg={error?.msg || deleteError?.msg} />
      <BulkSelectionBar
        count={selectedIds.size}
        singularLabel="schedule"
        pluralLabel="schedules"
        onClear={() => setSelectedIds(new Set())}
        onDelete={removeSelected}
        disabled={disableDelete || isDeleteLoading}
      />
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={(event) => toggleAll(event.target.checked)}
                  disabled={disableDelete || !pageIds.length}
                  inputProps={{ 'aria-label': 'Select all schedules' }}
                />
              </StyledTableCell>
              <StyledTableCell align="left" width={200}>
                <StyledTableSortLabel
                  active={sortby === SortEnum.name || true}
                  direction={sortby === SortEnum.name ? order : OrderEnum.asc}
                  onClick={() => sortOnClick(SortEnum.name)}
                >
                  Schedule name
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left">Note</StyledTableCell>
              <StyledTableCell align="left">
                Default presentation
              </StyledTableCell>
              <StyledTableCell align="left">Time zone</StyledTableCell>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortby === SortEnum.updatedAt || true}
                  direction={
                    sortby === SortEnum.updatedAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.updatedAt)}
                >
                  Last updated
                </StyledTableSortLabel>
              </StyledTableCell>

              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {schedulers.map((scheduler) => {
              return (
                <TableRow
                  key={scheduler._id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(scheduler._id)}
                      onChange={(event) =>
                        setSelected(scheduler._id, event.target.checked)
                      }
                      disabled={disableDelete}
                      inputProps={{
                        'aria-label': `Select ${scheduler.name}`,
                      }}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <NavLink
                      to={`/event/${scheduler._id}`}
                      state={{
                        search: location?.search,
                        timezone: scheduler.timeZone,
                      }}
                    >
                      {scheduler?.name}
                    </NavLink>
                  </TableCell>
                  <TableCell align="left">{scheduler?.description}</TableCell>
                  <TableCell align="left">
                    {scheduler?.defaultPresentation?.name}
                  </TableCell>
                  <TableCell align="left">{scheduler?.timeZone}</TableCell>
                  <TableCell align="left">
                    {displayDate(scheduler?.updatedAt)}
                  </TableCell>
                  <TableCell align="center">
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={0.75}
                    >
                      <Tooltip title="Edit schedule">
                        <span>
                          <IconButton
                            aria-label={`Edit ${scheduler.name}`}
                            disabled={disableDelete}
                            onClick={() => {
                              setActiveScheduler(scheduler);
                              setSchedulerEditModal(true);
                            }}
                            sx={{ ...actionButtonSx, color: '#2563eb' }}
                          >
                            <Edit3 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete schedule">
                        <span>
                          <IconButton
                            aria-label={`Delete ${scheduler.name}`}
                            disabled={disableDelete}
                            onClick={() => handleDelete(scheduler._id)}
                            sx={{
                              ...actionButtonSx,
                              color: '#dc2626',
                              bgcolor: '#fffafa',
                              borderColor: '#fecaca',
                            }}
                          >
                            <Trash2 size={15} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={list?.pagination.total === 0}
          count={list?.pagination.total || 0}
        />
      </TableContainer>
      <DialogModal
        title="Edit schedule"
        onClose={() => setSchedulerEditModal(false)}
        open={schedulerEditModal}
      >
        <SchedulerEditModal
          scheduler={activeScheduler}
          onFinished={() => {
            setSchedulerEditModal(false);
          }}
          refetch={refetch}
        />
      </DialogModal>
    </>
  );
};
export default SchedulerTable;
